import { useState, useRef } from 'react';
import { Upload, FileText, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { authAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import JourneyLayout from '../../components/JourneyLayout';
import { eventToRouteMap } from '../../Utils/constants';

function BankStatementUpload() {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const navigate = useNavigate();

  const handleFile = (file) => {
    if (!file) return;

    // Check if file is PDF
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setError('Please select a valid PDF file');
      return;
    }

    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      setError('File size must be less than 10MB');
      return;
    }

    setSelectedFile(file);
    setFileName(file.name);
    setFileSize(file.size);
    setError('');
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    handleFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    handleFile(file);
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        // Remove data:application/pdf;base64, prefix if present
        const base64Data = base64String.includes(',')
          ? base64String.split(',')[1]
          : base64String;
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a PDF file to upload');
      return;
    }

    const profileId = localStorage.getItem('profile_id') || '';
    if (!profileId) {
      setError('Session expired. Please login again.');
      setTimeout(() => navigate('/apply-now'), 2000);
      return;
    }

    setUploading(true);
    setError('');
    setUploadSuccess(false);

    try {
      // Convert file to base64
      const base64Data = await convertFileToBase64(selectedFile);

      // Call API
      const response = await authAPI.uploadBankStatement({
        profile_id: profileId,
        event_name: 'bank_statement_upload',
        file_ext: 'pdf',
        file: base64Data,
      });

      if (response.Status === 1) {
        setUploadSuccess(true);
        // Navigate to next page after 2 seconds
        setTimeout(() => {
          // Check next_event_name from response or navigate to dashboard
          const nextEvent = response.Data?.next_step;
          if (nextEvent && eventToRouteMap[nextEvent]) {
            navigate(eventToRouteMap[nextEvent]);
          } else {
            navigate('/banking-details');
          }
        }, 2000);
      } else {
        setError(response.Message || 'Failed to upload bank statement. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Failed to upload bank statement. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileName('');
    setFileSize(0);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <JourneyLayout journeyStep={6}>
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Upload Bank Statement
          </h1>
          <p className="text-gray-600 text-sm sm:text-base">
            Please upload your bank statement in PDF format
          </p>
        </div>

        {uploadSuccess ? (
          <div className="bg-green-50 border-2 border-green-200 rounded-3xl p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-green-100 p-4 rounded-full border-2 border-green-300">
                <CheckCircle className="h-12 w-12 text-green-600" />
              </div>
            </div>
            <h3 className="text-xl font-bold text-green-700 mb-2">Upload Successful!</h3>
            <p className="text-gray-700">Your bank statement has been uploaded successfully.</p>
            <p className="text-gray-500 text-sm mt-2">Redirecting...</p>
          </div>
        ) : (
          <>
            {/* File Upload Area */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-3xl p-8 transition-all ${
                dragActive
                  ? 'border-theme bg-theme/5'
                  : 'border-gray-300 bg-gradient-to-br from-gray-50 to-white hover:border-theme/50'
              }`}
            >
              <div className="flex flex-col items-center justify-center text-center">
                <div className="w-20 h-20 rounded-full bg-theme/10 border-2 border-theme/20 flex items-center justify-center mb-6">
                  <FileText className="h-10 w-10 text-theme" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  {selectedFile ? 'File Selected' : 'Upload Bank Statement'}
                </h2>
                <p className="text-gray-600 mb-6">
                  {selectedFile
                    ? `${fileName} (${formatFileSize(fileSize)})`
                    : 'Drag & drop your PDF file here or click to browse'}
                </p>

                {!selectedFile ? (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-gradient-to-r from-theme to-theme2 text-white px-8 py-3 rounded-2xl font-semibold hover:opacity-90 transition-all flex items-center gap-2 shadow-lg"
                  >
                    <Upload className="h-5 w-5" />
                    Browse Files
                  </button>
                ) : (
                  <div className="w-full space-y-4">
                    <div className="bg-white border-2 border-gray-200 rounded-2xl p-4 shadow-md">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileText className="h-6 w-6 text-theme" />
                          <div className="text-left">
                            <p className="text-gray-900 font-semibold">{fileName}</p>
                            <p className="text-gray-500 text-sm">{formatFileSize(fileSize)}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={handleRemoveFile}
                          className="text-red-500 hover:text-red-600 transition-colors px-3 py-1 font-semibold"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleUpload}
                      disabled={uploading}
                      className="w-full bg-gradient-to-r from-theme to-theme2 text-white px-8 py-4 rounded-2xl font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-5 w-5" />
                          Upload Bank Statement
                        </>
                      )}
                    </button>
                  </div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-blue-700 mb-3 flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Instructions
              </h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>Only PDF files are accepted</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>Maximum file size: 10MB</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>Make sure the bank statement is clear and readable</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>The statement should be recent (last 3-6 months)</span>
                </li>
              </ul>
            </div>

            {error && (
              <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 text-sm text-red-700">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </JourneyLayout>
  );
}

export default BankStatementUpload;
